/**
 * lib/financeCore.ts
 * ------------------------------------------------------------------
 * FEES / FINANCE — pure engine (P0). No Firestore, no React: types,
 * the collection registry, and the arithmetic the whole module trusts.
 *
 * Design anchors (mirroring the HR/payroll module):
 *   - Money via fmtPula / round2 (reused from hrPayroll — single
 *     rounding convention across the platform).
 *   - A rule-driven discount engine. Sibling discounts are keyed on
 *     `familyId` (the Families foundation) via a per-invoice sibling
 *     rank; prepayment is a rule too. Bursary/staff/manual discounts
 *     are applied as explicit line reductions.
 *   - One invariant, enforced in one place: every invoice satisfies
 *     `total = subtotal − discountTotal` and `balance = total − paid`.
 *   - Config-driven (settings + rules), so the open commercial
 *     decisions (formulae, buckets, invoice-number format) are data,
 *     not code — P1+ tunes them without touching this file.
 *
 * Everything here is unit-tested (see scripts/finance-core.test.ts).
 * ------------------------------------------------------------------ */
import { fmtPula, round2 } from '@/lib/hrPayroll';
import type { TermKey } from '@/lib/school.config';
import type { FamilyBillingPlan, BillingInstalmentRow } from '@/types';

import { isPreSchoolClass, termLabelForCohort } from './cohort';

export { fmtPula, round2 };

/** Display a money balance with credits shown in accounting brackets,
 *  e.g. (P1,234.00) for a credit. Display-only — never use in CSV/exports. */
export function fmtBalance(n: number): string {
  const v = round2(n);
  return v < 0 ? `(${fmtPula(-v)})` : fmtPula(v);
}

/* ============================ collections ============================ */
export const FINANCE_COLLECTIONS = {
  feeItems: 'financeFeeItems',
  feeStructures: 'financeFeeStructures',
  studentFeeOptions: 'financeStudentFeeOptions',
  invoices: 'financeInvoices',
  payments: 'financePayments',
  discountRules: 'financeDiscountRules',
  settings: 'financeSettings', // singleton doc id 'school'
  auditLog: 'financeAuditLog',
  academicHolds: 'financeAcademicHolds', // per-family parent-access hold override
  pendingSiblingDiscounts: 'financePendingSiblingDiscounts',
  appliedSiblingDiscounts: 'financeAppliedSiblingDiscounts',
  reconciliations: 'financeReconciliations', // daily/weekly/monthly cash-up closes
  unidentifiedPayments: 'financeUnidentifiedPayments', // bank credits pending allocation (suspense)
  // --- fee special cases ---------------------------------------------
  staffChildren: 'financeStaffChildren',   // per-staff: their children, for the staff-fee benefit
  prepayFamilies: 'financePrepayFamilies', // families paying a full year up front, per year
  bursaryTypes: 'financeBursaryTypes',     // the schemes a bursary can be awarded under
  bursaryAwards: 'financeBursaryAwards',   // per-pupil, per-year award of a % of tuition
  // --- adjustments ----------------------------------------------------
  studentDiscounts: 'financeStudentDiscounts', // manual per-pupil concessions (sibling etc.)
  adjustments: 'financeAdjustments',       // credit notes and write-offs
  counters: 'financeCounters',             // per-series document-number counters
  // --- period end -----------------------------------------------------
  recurringTemplates: 'financeRecurringTemplates', // standing entries, posted monthly by the sweep
  deferrals: 'financeDeferrals',           // prepayments and accruals, released month by month
} as const;

/* ------------------------- fee special cases ------------------------- *
 * Staff children, full-annual-payment and bursaries. Each is a reason a
 * pupil is billed differently from everyone else in their band, and each
 * needs the same two things: a record of who decided it, and a decision by
 * someone other than the person who arranged it.
 * -------------------------------------------------------------------- */

/** Approval state shared by every fee special case.
 *
 *  A case takes effect ONLY once 'approved'. That is enforced where it
 *  matters — at the billing feed, not merely in the screen — because a
 *  bursary is money off a bill and an unapproved one that quietly applies
 *  is indistinguishable from an approved one after the invoice is sent. */
export type FeeCaseStatus = 'pending' | 'approved' | 'rejected';

/** A staff member's enrolled children for the staff-fee benefit.
 *  Finance-owned and independent of any parent-portal account; doc id is
 *  the hrStaff id. */
export interface StaffChildrenLink {
  id: string;                 // staffId
  childStudentIds: string[];
  status?: FeeCaseStatus;
  approvedBy?: string;
  approvedByName?: string;
  approvedAt?: string;
  rejectedReason?: string;
  updatedAt?: string;
  updatedBy?: string;
}

/** A family paying the full year up front, for one year. Sits alongside the
 *  older per-family `billingPlan.kind === 'prepayYear'` rather than
 *  replacing it: the billing run reads the UNION of the two, so an
 *  arrangement recorded either way is honoured. */
export interface PrepayFamily {
  id: string;                 // `${familyId}_${year}`
  familyId: string;
  year: string;
  status?: FeeCaseStatus;
  createdBy?: string;
  createdByName?: string;
  createdAt?: string;
  approvedBy?: string;
  approvedByName?: string;
  approvedAt?: string;
  rejectedReason?: string;
  updatedAt?: string;
}

/** A configurable bursary scheme. */
export interface BursaryType {
  id: string;
  name: string;
  description?: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** A per-pupil, per-year bursary: a % of TUITION, taken after the sibling
 *  discount. Applies only once approved. */
export interface BursaryAward {
  id: string;                 // `${year}_${studentId}_${bursaryTypeId}`
  studentId: string;
  studentName?: string;
  bursaryTypeId: string;
  bursaryTypeName?: string;
  year: string;
  percent: number;            // 0..100
  status?: FeeCaseStatus;
  createdBy?: string;
  createdByName?: string;
  createdAt?: string;
  approvedBy?: string;
  approvedByName?: string;
  approvedAt?: string;
  rejectedReason?: string;
  updatedAt?: string;
}

/** Has this case been approved? Anything other than an explicit 'approved'
 *  is not — including a record written before `status` existed, which is
 *  deliberately treated as pending rather than grandfathered in. */
export const feeCaseApproved = (c: { status?: FeeCaseStatus } | undefined | null): boolean =>
  c?.status === 'approved';

export const FINANCE_SETTINGS_ID = 'school';

/* ============================== types =============================== */
export type FeeCategory =
  | 'tuition' | 'boarding' | 'transport' | 'meals'
  | 'activity' | 'uniform' | 'exam' | 'registration' | 'other';

export const FEE_CATEGORY_LABELS: Record<FeeCategory, string> = {
  tuition: 'Tuition',
  boarding: 'Boarding',
  transport: 'Transport',
  meals: 'Meals',
  activity: 'Activities',
  uniform: 'Uniform',
  exam: 'Examinations',
  registration: 'Registration',
  other: 'Other',
};

export type FeeRecurrence = 'term' | 'annual' | 'once';

/** A catalog entry: a chargeable thing with a default price. */
export interface FeeItem {
  id: string;
  name: string;
  category: FeeCategory;
  amount: number;
  recurrence: FeeRecurrence;
  /** Mandatory items bill automatically; optional ones require opt-in. */
  optional: boolean;
  active: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export type DiscountKind = 'sibling' | 'prepayment' | 'bursary' | 'staff' | 'manual';

export const DISCOUNT_KIND_LABELS: Record<DiscountKind, string> = {
  sibling: 'Sibling discount',
  prepayment: 'Prepayment discount',
  bursary: 'Bursary',
  staff: 'Staff child',
  manual: 'Manual adjustment',
};

/** A configurable discount. `percent` XOR `flat`. */
export interface DiscountRule {
  id: string;
  kind: DiscountKind;
  label: string;
  active: boolean;
  percent?: number; // e.g. 10 → 10%
  flat?: number; // fixed BWP reduction
  /** Sibling: apply from this 1-based rank onward (default 2 = 2nd child). */
  siblingOrderFrom?: number;
  /** Which charges the discount reduces (default 'tuition'). */
  appliesToCategory?: FeeCategory | 'all';
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceLine {
  feeItemId?: string;
  label: string;
  category: FeeCategory;
  amount: number;
}

export interface InvoiceDiscount {
  ruleId?: string;
  kind: DiscountKind;
  label: string;
  amount: number; // positive = reduction
  /**
   * The charge this reduction was taken FROM.
   *
   * Without it an invoice records that P6,000 came off but not off what, so
   * nothing downstream can tell whether a further concession has room left.
   * That is how a 60% bursary on the invoice and a 50% sibling credit raised
   * afterwards could between them exceed the tuition they are both measured
   * against. Absent on invoices raised before Sep 2026; readers treat that
   * as 'all', which is the cautious reading.
   */
  appliesToCategory?: FeeCategory | 'all';
}

export type InvoiceStatus = 'draft' | 'issued' | 'part_paid' | 'paid' | 'void';

/** A scheduled portion of an invoice's total, with its own due date.
 *  Instalment amounts sum to the invoice total; payments are applied to the
 *  invoice as a whole and allocated to instalments oldest-first. */
export interface Instalment {
  label: string;
  dueDate: string; // ISO date
  amount: number;
}

export interface Invoice {
  id: string;
  invoiceNo: string;
  studentId: string;
  studentName?: string;
  familyId?: string;
  term: TermKey;
  year: string;
  issuedAt: string;
  dueDate: string;
  /** Optional payment schedule. When present, due/overdue is computed per
   *  instalment; dueDate remains the final/overall due date. */
  instalments?: Instalment[];
  lines: InvoiceLine[];
  discounts: InvoiceDiscount[];
  subtotal: number;
  discountTotal: number;
  total: number;
  paid: number;
  /** Credited or written off — reduces the balance without money arriving.
   *  Absent on every invoice raised before adjustments existed, which is why
   *  everything that reads it treats absent as nil. */
  adjusted?: number;
  /**
   * WHICH GROUP THIS INVOICE WAS RAISED FOR.
   *
   * A-level takes an intake in January and another in June, and the June
   * intake's Term 2 runs while the school is in Term 3. `term` stays a
   * TermKey so every guard and grouping still works; this says which
   * calendar that term belongs to, so a document a parent reads can say
   * "Term 2 (June Intake)" rather than leaving them to wonder.
   *
   * Absent on everything billed before Sep 2026, and on the main school,
   * where it would say nothing.
   */
  cohort?: 'main' | 'alJan' | 'alJune' | 'preSchool';
  /**
   * WHAT THIS DOCUMENT IS. 'invoice' = the school billed something.
   * 'opening' = a position brought forward, not a charge.
   *
   * 803 of 1,359 documents in this collection are the 11 Aug 2026 rebaseline
   * of the Pastel age analysis, and 182 of those carry a negative total — a
   * credit the school holds, in a document shaped like a debt. Summed with
   * real invoices they misstate what a term billed by P3.08m, and posted to
   * the ledger they would put a credit balance on the debit side of debtors.
   *
   * Read it through `isOpeningBalance`, never directly: the field is being
   * backfilled and absent means "work it out from the reference".
   */
  kind?: 'invoice' | 'opening';
  balance: number;
  status: InvoiceStatus;
  createdAt: string;
  updatedAt: string;
}

export type PaymentMethod = 'cash' | 'eft' | 'card' | 'mobile' | 'other';

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: 'Cash',
  eft: 'Bank transfer (EFT)',
  card: 'Card',
  mobile: 'Mobile money',
  other: 'Other',
};

export interface Payment {
  id: string;
  invoiceId: string;
  studentId: string;
  studentName?: string;
  familyId?: string;
  amount: number;
  method: PaymentMethod;
  reference?: string;
  receiptNo?: string;
  receivedAt: string;
  recordedBy?: string;        // cashier uid
  recordedByName?: string;    // cashier display name, denormalised at capture
  note?: string;
  /** Stable id of the originating bank-statement line, when this payment
   *  was posted via bank reconciliation. Used to prevent double-posting
   *  when the same statement is re-imported. */
  statementLineId?: string;
  /* ---- Reversal (payment void) ------------------------------------
   * A reversal is a *ledger entry*, not a delete: the original payment is
   * preserved and flagged `reversed`, and a matching negative payment
   * (`isReversal`, `amount = -original`) is written so every sum that
   * trusts `amount` (invoice.paid, statements, cash-up) nets automatically. */
  /** On the ORIGINAL payment once it has been reversed. */
  reversed?: boolean;
  reversedAt?: string;
  reversedBy?: string;
  reversedByName?: string;
  reversedReason?: string;
  /** Id of the negative reversal entry created against this payment. */
  reversalPaymentId?: string;
  /** On the REVERSAL entry: true, plus the id of the payment it reverses. */
  isReversal?: boolean;
  reversalOf?: string;
  createdAt: string;
  updatedAt: string;
}

/* ---- Unidentified payments (suspense) ----------------------------
 * A bank credit that arrived without a usable reference — money we HAVE
 * but can't yet attribute to a student. It sits here (a suspense holding
 * area) until the real destination is discovered, then it is "settled":
 * a normal Payment is captured against the chosen invoice and this record
 * is flagged `settled` with links back to that payment/invoice. It is
 * NOT a Payment and never touches an invoice balance until settled. */
export type UnidentifiedStatus = 'open' | 'settled';

export interface UnidentifiedPayment {
  id: string;
  amount: number;
  method: PaymentMethod;
  /** ISO date the bank credit landed. */
  receivedAt: string;
  /** Whatever reference the bank line carried (often junk / a phone number). */
  bankRef?: string;
  /** Free text — the payer / narration as it appeared on the statement. */
  description?: string;
  note?: string;
  status: UnidentifiedStatus;
  /** On settle: the real payment + invoice this credit was allocated to. */
  settledInvoiceId?: string;
  settledInvoiceNo?: string;
  settledStudentName?: string;
  settledPaymentId?: string;
  settledAt?: string;
  settledBy?: string;
  settledByName?: string;
  createdAt: string;
  createdBy?: string;
  createdByName?: string;
  updatedAt: string;
}

/* ---- Cash-up reconciliation (daily/weekly/monthly close) ---------- */
export type ReconcileMode = 'day' | 'week' | 'month';

/** System-computed expected totals per payment method for the period. */
export type ReconcileMethodTotals = Record<PaymentMethod, number>;

/** A closed (reconciled) cash-up period. One record per (mode, periodKey);
 *  the id encodes both so a period cannot be closed twice. */
export interface FinanceReconciliation {
  id: string;                 // `${mode}_${periodKey}`  e.g. day_2026-06-27
  mode: ReconcileMode;
  periodKey: string;          // YYYY-MM-DD (day / week-start) or YYYY-MM (month)
  periodStart: string;        // ISO, inclusive
  periodEnd: string;          // ISO, exclusive
  systemTotals: ReconcileMethodTotals; // expected, by method, at close
  receiptCount: number;
  grandTotal: number;
  counted: { cash: number };   // physically counted/banked (cash only)
  variance: { cash: number };  // counted − expected
  bankingRef?: string;
  note?: string;
  varianceReason?: string;    // required when variance ≠ 0
  status: 'reconciled' | 'reopened';
  reconciledBy: string;       // uid
  reconciledByEmail?: string;
  reconciledAt: string;       // ISO
  reopenedBy?: string;
  reopenedByEmail?: string;
  reopenedAt?: string;
  reopenReason?: string;
  createdAt: string;
  updatedAt: string;
}

export function reconciliationId(mode: ReconcileMode, periodKey: string): string {
  return `${mode}_${periodKey}`;
}

export interface FinanceSettings {
  id: string; // 'school'
  currency: 'BWP';
  /** Invoice number: tokens {PREFIX} {YEAR} {TERM} {SEQ}. */
  invoiceNoFormat: string;
  invoiceNoPrefix: string;
  /** Days from issue to due date. */
  dueDays: number;
  /** Aging thresholds in days (ascending), e.g. [30,60,90]. */
  agingThresholds: number[];
  /**
   * THE DAY THE LEDGER TAKES OVER. Every source document dated on or before
   * it was carried by the opening journal; everything after it posts through
   * its own trigger. Written by the Opening Balances screen when the opening
   * journal is posted, so the boundary and the entry that describes it cannot
   * drift apart. Absent means the school has not decided — and nothing posts.
   */
  glCutoverDate?: string;
  /**
   * Whether the posting triggers are live. Separate from the date on purpose:
   * bringing the opening position across must not silently start posting
   * every invoice the school raises. A human turns this on.
   */
  glPostingEnabled?: boolean;
  siblingDiscountPercent: number;
  siblingOrderFrom: number;
  prepaymentDiscountPercent: number;
  /** Master switch for emailing an invoice PDF to the parent when an invoice
   *  is generated. Absent/true = email as normal; false = the PDF is still
   *  rendered + stored, but no email is sent (temporary hold). */
  invoiceEmailEnabled?: boolean;
  /** Late-payment interest charged per month on overdue balances (LKC: 2%). */
  lateInterestMonthlyPct?: number;
  /** One-time fees collected up front at application/admission time and
   *  confirmed at approval (NOT re-billed on the joining invoice). */
  applicationFeeAmount?: number;
  registrationFeeAmount?: number;
  /** Fee reminders (P1): days BEFORE an instalment due date to remind
   *  parents, and days AFTER it falls overdue. Empty disables that side. */
  remindBeforeDays?: number[];
  remindAfterDays?: number[];
  remindersEnabled?: boolean;
  /**
   * The wording of the arrears reminder emails — the ones the 07:00 sweep
   * and the bulk send put in front of parents. Absent means the shipped
   * default in lib/reminderTemplate.ts, which is word for word what the
   * Cloud Function used to hardcode, so nothing changed for any parent
   * until the school chose to change it. Read per field, so a half-finished
   * edit does not take the body down with the subject.
   */
  reminderSubjectBefore?: string;
  reminderBodyBefore?: string;
  reminderSubjectAfter?: string;
  reminderBodyAfter?: string;
  /** Daily bursar digest of newly-overdue instalments. */
  bursarDigestEnabled?: boolean;
  /** Default instalment split (percentages summing to 100) applied to new
   *  term invoices when a family has no specific plan. [50,50] = two parts. */
  defaultInstalmentSplits?: number[];
  /** Default explicit instalment schedule (percent + day-offset from the
   *  term-start anchor) applied to the general billing run. Preferred over
   *  defaultInstalmentSplits when present. */
  defaultInstalmentPlan?: BillingInstalmentRow[];
  updatedAt: string;
}

export const DEFAULT_FINANCE_SETTINGS: FinanceSettings = {
  id: FINANCE_SETTINGS_ID,
  currency: 'BWP',
  invoiceNoFormat: '{PREFIX}/{YEAR}/{SEQ}',
  invoiceNoPrefix: 'INV',
  dueDays: 14,
  agingThresholds: [30, 60, 90],
  siblingDiscountPercent: 5,
  siblingOrderFrom: 2,
  prepaymentDiscountPercent: 2.5,
  invoiceEmailEnabled: true,
  lateInterestMonthlyPct: 2,
  applicationFeeAmount: 0,
  registrationFeeAmount: 0,
  remindBeforeDays: [7, 1],
  remindAfterDays: [1, 7, 14],
  remindersEnabled: true,
  bursarDigestEnabled: true,
  defaultInstalmentSplits: [50, 50],
  defaultInstalmentPlan: [{ percent: 50, offsetDays: 0 }, { percent: 50, offsetDays: 30 }],
  updatedAt: new Date(0).toISOString(),
};

/* ====================== invoice-number formatting ==================== */
export function formatInvoiceNo(
  settings: Pick<FinanceSettings, 'invoiceNoFormat' | 'invoiceNoPrefix'>,
  opts: { year: string; term?: TermKey; seq: number },
): string {
  const seq = String(opts.seq).padStart(4, '0');
  return settings.invoiceNoFormat
    .replace('{PREFIX}', settings.invoiceNoPrefix)
    .replace('{YEAR}', opts.year)
    .replace('{TERM}', opts.term ?? '')
    .replace('{SEQ}', seq)
    .replace(/\/+/g, '/')
    .replace(/\/$/, '');
}

/**
 * The part of an invoice number every invoice of a year shares — for the
 * school's format `{PREFIX}/{YEAR}/{SEQ}`, "INV/2026/". Everything before
 * the term or the sequence number, whichever comes first.
 *
 * WHY. From 10 Oct 2026 parents quote the pupil's name when paying, but
 * many had already paid quoting the invoice number. The Payments search
 * shows this part fixed, so the bursar types only "808" for INV/2026/0808.
 */
export function invoiceNoCommonPart(
  settings: Pick<FinanceSettings, 'invoiceNoFormat' | 'invoiceNoPrefix'>,
  year: string,
): string {
  const full = settings.invoiceNoFormat.replace('{PREFIX}', settings.invoiceNoPrefix).replace('{YEAR}', year);
  const cuts = ['{TERM}', '{SEQ}'].map((t) => full.indexOf(t)).filter((i) => i >= 0);
  const head = cuts.length ? full.slice(0, Math.min(...cuts)) : full;
  return head.replace(/\/+/g, '/');
}

/**
 * Does this invoice number match what the bursar typed after the common
 * part? "808", "0808" and "80" all find INV/2026/0808 (leading zeros are
 * not needed); a whole number pasted in, common part and all, works too.
 * Returns 2 for an exact match, 1 for a partial one, 0 for none, so exact
 * matches can be listed first.
 */
export function invoiceNoTailMatch(invoiceNo: string, common: string, typed: string): 0 | 1 | 2 {
  const no = String(invoiceNo ?? '');
  if (!no.toUpperCase().startsWith(common.toUpperCase())) return 0;
  let t = typed.trim();
  if (t.toUpperCase().startsWith(common.toUpperCase())) t = t.slice(common.length);
  if (!t) return 1;
  const rest = no.slice(common.length);
  const strip = (s: string) => s.replace(/^0+(?=.)/, '').toLowerCase();
  if (strip(rest) === strip(t)) return 2;
  return strip(rest).startsWith(strip(t)) ? 1 : 0;
}

/* ========================== discount engine ========================= */
export interface DiscountContext {
  /** 1-based rank of this student among billed siblings in the family. */
  siblingRank?: number;
  /** True if this invoice is being paid/issued for the full year upfront. */
  prepayFullYear?: boolean;
  /** True only when an operations/finance admin has approved this family's
   *  sibling discount. The sibling discount is withheld until then. */
  siblingApproved?: boolean;
}

export function lineCategoryTotal(lines: InvoiceLine[], cat: FeeCategory | 'all'): number {
  return round2(lines.filter((l) => cat === 'all' || l.category === cat).reduce((a, l) => a + (l.amount || 0), 0));
}

/**
 * Apply the active, rule-driven discounts to a set of lines for one
 * student. Pure: caller supplies the family/prepay context.
 */
/* ------------------ manual, per-pupil discounts ------------------
   The bursar's own instrument, replacing the automatic sibling rule.

   The MATH lives here because this module owns lineCategoryTotal and the
   invoice shape; the Firestore document, its validation and its labels live
   in lib/studentDiscounts.ts, which imports from here. One direction only —
   the two used to be a circular import waiting to happen. */

/** A concession the bursar recorded against one pupil. */
export interface ManualDiscount {
  kind: DiscountKind;
  /** 'percent' scales with the fee; 'amount' is the figure that was promised. */
  mode: 'percent' | 'amount';
  value: number;
  appliesToCategory: FeeCategory | 'all';
  label?: string;
}

/**
 * Turn a pupil's concessions into invoice discount lines.
 *
 * NEVER EXCEEDS THE BASE. A P5,000 concession against P3,000 of tuition
 * takes 3,000. Letting it take 5,000 pushes the invoice negative, which
 * reads as the school owing a family money it never received and posts a
 * debit to receivables that no payment can ever clear.
 *
 * Concessions on the SAME base consume each other's room, in order, so a
 * 60% sibling discount and a P4,000 bursary against P5,000 of tuition take
 * P5,000 between them and not P7,000.
 */
export function applyManualDiscounts(
  discounts: ManualDiscount[],
  lines: InvoiceLine[],
): InvoiceDiscount[] {
  const out: InvoiceDiscount[] = [];
  const usedByBase = new Map<string, number>();

  for (const d of discounts) {
    const base = lineCategoryTotal(lines, d.appliesToCategory);
    if (base <= 0) continue;

    const raw = d.mode === 'percent'
      ? round2((base * Math.min(100, Math.max(0, Number(d.value) || 0))) / 100)
      : round2(Math.max(0, Number(d.value) || 0));
    if (raw <= 0) continue;

    const key = String(d.appliesToCategory);
    const used = usedByBase.get(key) ?? 0;
    const room = Math.max(0, round2(base - used));
    const amount = round2(Math.min(raw, room));
    if (amount <= 0) continue;

    usedByBase.set(key, round2(used + amount));
    out.push({ kind: d.kind, label: d.label || 'Discount', amount, appliesToCategory: d.appliesToCategory });
  }
  return out;
}

export function computeDiscounts(
  lines: InvoiceLine[],
  ctx: DiscountContext,
  rules: DiscountRule[],
): InvoiceDiscount[] {
  const subtotal = lineCategoryTotal(lines, 'all');
  const out: InvoiceDiscount[] = [];

  for (const rule of rules) {
    if (!rule.active) continue;
    const cat = rule.appliesToCategory ?? (rule.kind === 'sibling' ? 'tuition' : 'all');
    const base = lineCategoryTotal(lines, cat);

    /* THE AUTOMATIC SIBLING RULE IS RETIRED (Aug 2026).
       It needed three things right before a pula came off a bill — a family,
       a birth-order rank within it, and an approval — and the first was built
       partly from SURNAME, which had put five unrelated Molefes in one
       household. Sibling concessions are now recorded per pupil by the
       bursar and arrive via `manualDiscountsByStudent`. Nothing was lost in
       the switch: no sibling discount had ever been applied.

       The branch is left here, inert, rather than deleted, because a
       DiscountRule of kind 'sibling' may still exist in the settings and
       must now do nothing rather than quietly resume. */
    if (rule.kind === 'sibling') {
      continue;
    } else if (rule.kind === 'prepayment') {
      if (!ctx.prepayFullYear || base <= 0) continue;
      const amount = rule.flat != null ? Math.min(rule.flat, base) : round2((base * (rule.percent ?? 0)) / 100);
      if (amount > 0) out.push({ ruleId: rule.id, kind: 'prepayment', label: rule.label, amount, appliesToCategory: cat });
    }
    // bursary / staff / manual are applied as explicit discounts by the caller.
  }

  // Never discount below zero in aggregate.
  const cap = subtotal;
  let running = 0;
  return out.map((d) => {
    const room = Math.max(0, cap - running);
    const amt = Math.min(d.amount, room);
    running += amt;
    return { ...d, amount: round2(amt) };
  }).filter((d) => d.amount > 0);
}

/* ========================== invoice assembly ======================== */
export interface AssembleInvoiceInput {
  id: string;
  invoiceNo: string;
  studentId: string;
  studentName?: string;
  familyId?: string;
  term: TermKey;
  year: string;
  issuedAt: string;
  dueDate: string;
  lines: InvoiceLine[];
  rules: DiscountRule[];
  ctx?: DiscountContext;
  /** Bursary / staff / manual reductions decided by the bursar. */
  manualDiscounts?: InvoiceDiscount[];
  /** Optional payment schedule (parts must sum to the invoice total). */
  instalments?: Instalment[];
  status?: InvoiceStatus;
}

/** Build a fresh invoice (paid = 0) with all totals reconciled. */
export function assembleInvoice(input: AssembleInvoiceInput): Invoice {
  const subtotal = round2(input.lines.reduce((a, l) => a + (l.amount || 0), 0));
  const auto = computeDiscounts(input.lines, input.ctx ?? {}, input.rules);
  const discounts = [...auto, ...(input.manualDiscounts ?? [])].filter((d) => d.amount > 0);
  const discountTotal = round2(Math.min(subtotal, discounts.reduce((a, d) => a + d.amount, 0)));
  const total = round2(subtotal - discountTotal);
  const paid = 0;
  const balance = round2(total - paid);
  const now = new Date().toISOString();
  return {
    id: input.id,
    invoiceNo: input.invoiceNo,
    studentId: input.studentId,
    studentName: input.studentName,
    familyId: input.familyId,
    term: input.term,
    year: input.year,
    issuedAt: input.issuedAt,
    dueDate: input.dueDate,
    instalments: input.instalments && input.instalments.length > 0 ? input.instalments : undefined,
    lines: input.lines,
    discounts,
    subtotal,
    discountTotal,
    total,
    paid,
    balance,
    status: input.status ?? (total <= 0 ? 'paid' : 'issued'),
    createdAt: now,
    updatedAt: now,
  };
}

export function invoiceStatus(
  total: number,
  paid: number,
  voided = false,
  adjusted = 0,
): InvoiceStatus {
  if (voided) return 'void';
  /* An adjustment settles part of the invoice without money arriving: a credit
     note because the school should not have billed it, a write-off because the
     debt will not be collected. Either way the family owes that much less, so
     it counts towards settling the invoice exactly as a payment does — an
     invoice fully credited must read 'paid', not sit forever as 'issued'.

     Defaults to 0, so an invoice with no adjustment behaves precisely as
     before. Every one of the 807 live invoices is in that position today. */
  const settled = round2((Number(paid) || 0) + (Number(adjusted) || 0));
  if (settled <= 0) return total <= 0 ? 'paid' : 'issued';
  if (settled >= round2(total)) return 'paid';
  return 'part_paid';
}

/** Re-derive paid/balance/status from the invoice's payments. Pure. */
export function recomputeInvoice(invoice: Invoice, payments: Payment[]): Invoice {
  const paid = round2(payments.filter((p) => p.invoiceId === invoice.id).reduce((a, p) => a + (p.amount || 0), 0));
  // Credit notes and write-offs reduce the balance without money arriving.
  // Absent on every invoice raised before adjustments existed, and `|| 0`
  // keeps those computing exactly as they did.
  const adjusted = round2(Number(invoice.adjusted) || 0);
  const balance = round2(invoice.total - paid - adjusted);
  return {
    ...invoice,
    paid,
    balance,
    status: invoiceStatus(invoice.total, paid, invoice.status === 'void', adjusted),
    updatedAt: new Date().toISOString(),
  };
}

/* ============================== aging =============================== */
export interface AgingBucket { label: string; amount: number; }
export interface Aging {
  current: number;
  buckets: AgingBucket[]; // past-due tiers, ascending
  total: number;
}

const DAY = 86400000;

export function daysPastDue(dueDate: string, asOf: Date): number {
  const due = new Date(dueDate).getTime();
  if (Number.isNaN(due)) return 0;
  return Math.floor((asOf.getTime() - due) / DAY);
}

/* ===================== instalment schedule (P1) ==================== */

export interface InstalmentState {
  label: string;
  dueDate: string;
  amount: number;
  paid: number;
  balance: number;
  daysPastDue: number;
  status: 'paid' | 'upcoming' | 'due' | 'overdue';
}

/** Split a total into instalments on the given due dates. Equal split, with
 *  the final instalment absorbing any rounding remainder so the parts sum
 *  exactly to `total`. */
export function evenInstalments(total: number, dueDates: string[], labels?: string[]): Instalment[] {
  const n = dueDates.length;
  if (n <= 0) return [];
  const each = round2(total / n);
  return dueDates.map((dueDate, i) => ({
    label: labels?.[i] ?? `Instalment ${i + 1} of ${n}`,
    dueDate,
    amount: i === n - 1 ? round2(total - each * (n - 1)) : each,
  }));
}

/** Allocate the invoice's paid amount across instalments oldest-first and
 *  classify each. If the invoice has no schedule, the whole invoice is
 *  treated as one instalment on its dueDate. Pure. */
export function instalmentStates(inv: Invoice, asOf: Date = new Date()): InstalmentState[] {
  const sched: Instalment[] = inv.instalments && inv.instalments.length > 0
    ? [...inv.instalments].sort((a, b) => a.dueDate.localeCompare(b.dueDate))
    : [{ label: 'Full balance', dueDate: inv.dueDate, amount: inv.total }];
  let remainingPaid = round2(inv.paid ?? 0);
  return sched.map((ins) => {
    const paid = round2(Math.min(remainingPaid, ins.amount));
    remainingPaid = round2(remainingPaid - paid);
    const balance = round2(ins.amount - paid);
    const dpd = daysPastDue(ins.dueDate, asOf);
    const status: InstalmentState['status'] =
      balance <= 0 ? 'paid' : dpd > 0 ? 'overdue' : dpd === 0 ? 'due' : 'upcoming';
    return { label: ins.label, dueDate: ins.dueDate, amount: ins.amount, paid, balance, daysPastDue: Math.max(0, dpd), status };
  });
}

/** Currently-overdue amount on an invoice by instalment due dates (sum of
 *  unpaid balances of instalments already past due). More accurate than the
 *  single invoice dueDate when a schedule is present. */
export function instalmentOverdue(inv: Invoice, asOf: Date = new Date()): number {
  if (inv.status === 'void') return 0;
  return round2(instalmentStates(inv, asOf).filter((x) => x.status === 'overdue').reduce((a, x) => a + x.balance, 0));
}

/** The next unpaid instalment (soonest due with a balance), or null. */
export function nextInstalmentDue(inv: Invoice, asOf: Date = new Date()): InstalmentState | null {
  return instalmentStates(inv, asOf).find((x) => x.balance > 0) ?? null;
}

/** Named splits the bursar can pick at billing time or per invoice. */
export const INSTALMENT_PRESETS: { id: string; label: string; splits: number[] }[] = [
  { id: '50-50', label: '50 / 50 (two parts)', splits: [50, 50] },
  { id: '80-20', label: '80 / 20', splits: [80, 20] },
  { id: 'full', label: 'Pay in full (one part)', splits: [100] },
];

/** Single up-front schedule used for families paying the year in advance. */
export const PREPAY_LABEL = 'Paid in advance';

/** Build an instalment schedule from percentage splits and matching due
 *  dates. Amounts follow the splits; the final instalment absorbs any
 *  rounding so the parts sum exactly to `total`. Pure. */
export function buildInstalments(total: number, splits: number[], dueDates: string[], labels?: string[]): Instalment[] {
  const n = splits.length;
  if (n === 0 || dueDates.length < n) return [];
  const sumPct = splits.reduce((a, b) => a + b, 0) || 100;
  const amts = splits.map((pct) => round2((total * pct) / sumPct));
  const allButLast = round2(amts.slice(0, -1).reduce((a, x) => a + x, 0));
  amts[n - 1] = round2(total - allButLast);
  return splits.map((_, i) => ({
    label: labels?.[i] ?? (n === 1 ? 'Full payment' : `Instalment ${i + 1} of ${n}`),
    dueDate: dueDates[i],
    amount: amts[i],
  }));
}

/** Spread `n` due dates between (issue + firstOffsetDays) and finalDueDate,
 *  evenly. n=1 returns just the final date. Dates are YYYY-MM-DD. Pure. */
export function spreadDueDates(issuedAt: string, finalDueDate: string, n: number, firstOffsetDays = 14): string[] {
  const fin = finalDueDate.slice(0, 10);
  if (n <= 1) return [fin];
  const start = new Date(issuedAt); start.setDate(start.getDate() + firstOffsetDays);
  const startMs = start.getTime();
  const endMs = new Date(finalDueDate).getTime();
  const out: string[] = [];
  for (let i = 0; i < n; i++) {
    const t = startMs + (endMs - startMs) * (i / (n - 1));
    out.push(new Date(t).toISOString().slice(0, 10));
  }
  return out;
}

/* ===================== explicit instalment plans ===================== */

export type InstalmentPlanRow = BillingInstalmentRow; // { percent, offsetDays }

export const DEFAULT_INSTALMENT_PLAN: InstalmentPlanRow[] = [
  { percent: 50, offsetDays: 0 },
  { percent: 50, offsetDays: 30 },
];

/** anchorISO + offsetDays → YYYY-MM-DD. */
export function offsetDate(anchorISO: string, offsetDays: number): string {
  const d = new Date(anchorISO);
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

/** Build instalments from an explicit plan (percent + day-offset) anchored
 *  to a term-start date. Percentages need not sum to exactly 100 — they're
 *  normalised by buildInstalments. */
export function instalmentsFromPlan(total: number, anchorISO: string, plan: InstalmentPlanRow[]): Instalment[] {
  const rows = plan.length > 0 ? plan : DEFAULT_INSTALMENT_PLAN;
  const splits = rows.map((r) => r.percent);
  const dueDates = rows.map((r) => offsetDate(anchorISO, r.offsetDays));
  return buildInstalments(total, splits, dueDates);
}

/** Legacy %-splits → plan rows (first at anchor, then +30 days each). */
export function splitsToPlanRows(splits: number[]): InstalmentPlanRow[] {
  return splits.map((percent, i) => ({ percent, offsetDays: i * 30 }));
}

/** A billing plan → explicit plan rows, or null when it implies the default.
 *  prepayYear collapses to a single up-front instalment. */
export function planToRows(plan: FamilyBillingPlan | undefined): InstalmentPlanRow[] | null {
  if (!plan) return null;
  if (plan.kind === 'prepayYear') return [{ percent: 100, offsetDays: 0 }];
  if (plan.instalments && plan.instalments.length > 0) return plan.instalments;
  if (plan.splits && plan.splits.length > 0) return splitsToPlanRows(plan.splits);
  return null;
}

/** Resolve the effective schedule for a student: per-student override >
 *  family plan > the run's general schedule. */
export function resolveInstalmentRows(args: {
  studentPlan?: FamilyBillingPlan;
  familyPlan?: FamilyBillingPlan;
  generalRows: InstalmentPlanRow[];
}): InstalmentPlanRow[] {
  return planToRows(args.studentPlan) ?? planToRows(args.familyPlan) ?? args.generalRows;
}


/**
 * Age the outstanding balance of a set of invoices into buckets defined
 * by `thresholds` (e.g. [30,60,90] → current, 1–30, 31–60, 61–90, 90+).
 */
export function ageInvoices(invoices: Invoice[], thresholds: number[], asOf: Date = new Date()): Aging {
  const sorted = [...thresholds].sort((a, b) => a - b);
  const buckets: AgingBucket[] = [];
  for (let i = 0; i < sorted.length; i++) {
    const lo = i === 0 ? 1 : sorted[i - 1] + 1;
    buckets.push({ label: `${lo}–${sorted[i]} days`, amount: 0 });
  }
  buckets.push({ label: `${sorted[sorted.length - 1] + 1}+ days`, amount: 0 });

  let current = 0;
  let total = 0;
  for (const inv of invoices) {
    if (inv.status === 'void' || inv.balance <= 0) continue;
    total = round2(total + inv.balance);
    const d = daysPastDue(inv.dueDate, asOf);
    if (d <= 0) { current = round2(current + inv.balance); continue; }
    let placed = false;
    for (let i = 0; i < sorted.length; i++) {
      if (d <= sorted[i]) { buckets[i].amount = round2(buckets[i].amount + inv.balance); placed = true; break; }
    }
    if (!placed) buckets[buckets.length - 1].amount = round2(buckets[buckets.length - 1].amount + inv.balance);
  }
  return { current: round2(current), buckets, total: round2(total) };
}

/* ===================== sibling ranking (billing) ==================== */
/**
 * Assign a stable 1-based rank to each student within their family, so
 * the sibling discount targets the 2nd child onward deterministically.
 * Ranking is by admission number when present, else name — stable and
 * reproducible across billing runs.
 */
export function familySiblingRanks(
  members: { id: string; sortKey: string }[],
): Record<string, number> {
  const ordered = [...members].sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  const ranks: Record<string, number> = {};
  ordered.forEach((m, i) => { ranks[m.id] = i + 1; });
  return ranks;
}

/* ===================== fee structures (P1) ========================= */
/**
 * A FeeStructure is the set of charges for one (year, form-band). LKC
 * bills tuition per form band — Forms 1–3 / Form 4 / Form 5 / A-Level —
 * with shared add-ons (boarding, transport…) layered on. Mandatory
 * lines bill automatically; optional lines require a per-student opt-in.
 */
export interface FeeStructureLine {
  feeItemId?: string;
  label: string;
  category: FeeCategory;
  amount: number;
  mandatory: boolean;
}
export interface FeeStructure {
  id: string; // `${year}_${bandKey}`
  year: string;
  bandKey: string;
  label: string;
  lines: FeeStructureLine[];
  active: boolean;
  /** Terms this band is billed in. Omitted/empty = every term. Exam-year
   *  bands (Form 5, A-Level) bill only Term1 & Term2 — no Term 3. */
  terms?: TermKey[];
  createdAt: string;
  updatedAt: string;
}

export const FORM_BANDS: { key: string; label: string }[] = [
  { key: 'F1', label: 'Form 1' },
  { key: 'F2_3', label: 'Forms 2–3' },
  { key: 'F4', label: 'Form 4' },
  { key: 'F5', label: 'Form 5' },
  /* A-LEVEL, FOUR WAYS, ALL OPTIONAL.
   *
   * AS and A2 may be priced differently, and so may the June intake, which
   * runs its own calendar. The school asked for the ABILITY to differ, not
   * for four different fees — so `AL` remains and covers whichever of the
   * four are left unset. A band nobody fills in costs nothing. */
  { key: 'AL', label: 'A-Level — all (AS & A2, both intakes)' },
  { key: 'AL_AS', label: 'A-Level — AS' },
  { key: 'AL_AS_JUN', label: 'A-Level — AS, June intake' },
  { key: 'AL_A2', label: 'A-Level — A2' },
  { key: 'AL_A2_JUN', label: 'A-Level — A2, June intake' },
  /* PRE-SCHOOL (Oct 2026). Two classes, two fees: lower and upper. Until
     these existed the Day Care class (form "DC") fell through to Forms 2–3,
     and the 19 Sep Term 3 run invoiced every pre-school child P22,000. */
  { key: 'PRE_LOWER', label: 'Pre-School — Lower' },
  { key: 'PRE_UPPER', label: 'Pre-School — Upper' },
];

/** Pre-school classes: one definition, in lib/cohort. */
export { isPreSchoolClass };

/**
 * WHICH FEE STRUCTURES A BAND MAY FALL BACK ON, most specific first.
 *
 * `AL_AS_JUN` tries the June AS fee, then the AS fee, then A-level as a
 * whole. So the bursar can set one A-level fee and be done, or set one for
 * the June intake alone, without every combination having to exist.
 *
 * It matters that this is a CHAIN rather than a default. A pupil whose exact
 * band has no structure is not billed at all — planBillingRun returns
 * hasStructure:false — so splitting the band without this would have
 * silently stopped billing all 132 A-level pupils the moment it shipped.
 */
export function bandChain(bandKey: string): string[] {
  switch (bandKey) {
    case 'AL_AS_JUN': return ['AL_AS_JUN', 'AL_AS', 'AL'];
    case 'AL_A2_JUN': return ['AL_A2_JUN', 'AL_A2', 'AL'];
    case 'AL_AS': return ['AL_AS', 'AL'];
    case 'AL_A2': return ['AL_A2', 'AL'];
    default: return [bandKey];
  }
}

/**
 * The band a CLASS is billed on — form and calendar together.
 *
 * formBandForForm still answers for a bare form value and still returns 'AL'
 * for A-level, which is what every non-billing caller wants. This is the
 * billing view: it can tell AS from A2 and January from June, because those
 * are the four fees the school may want to set separately.
 */
export function bandForClass(
  cls?: { form?: unknown; name?: string; calendar?: 'january' | 'june' } | null,
): string {
  // Pre-school before anything else: "DC" has no form number, and the
  // fall-through below would price it as Forms 2–3.
  if (isPreSchoolClass(cls)) return /\bupper\b/i.test(String(cls?.name ?? '')) ? 'PRE_UPPER' : 'PRE_LOWER';
  const base = formBandForForm(cls?.form);
  if (base !== 'AL') return base;
  const s = `${cls?.form ?? ''} ${cls?.name ?? ''}`.toUpperCase();
  /* Word-bounded, so a class called "Junior" is never read as June and
     "Form 2 A" is never read as A2. */
  const june = cls?.calendar === 'june'
    || /\bJUNE\b|\bJUN\b/.test(String(cls?.name ?? '').toUpperCase());
  const isA2 = /\bA2\b/.test(s);
  if (isA2) return june ? 'AL_A2_JUN' : 'AL_A2';
  return june ? 'AL_AS_JUN' : 'AL_AS';
}


/** Map a class `form` value (number, or 'AS'/'A2'/…) to its band key. */
export function formBandForForm(form: unknown): string {
  const s = String(form ?? '').toUpperCase();
  if (s.includes('AS') || s.includes('A2') || s.includes('A-LEVEL') || s.includes('ALEVEL')) return 'AL';
  const m = s.match(/[1-6]/);
  const n = m ? Number(m[0]) : 0;
  if (n === 6) return 'AL';
  if (n === 5) return 'F5';
  if (n === 4) return 'F4';
  if (n === 1) return 'F1';
  return 'F2_3'; // Forms 2 & 3 (and any unrecognised lower form)
}
export function formBandLabel(key: string): string {
  return FORM_BANDS.find((b) => b.key === key)?.label ?? key;
}

/** One-time development fee, by the band a pupil is ADMITTED into. Paid once
 *  in a pupil's life at the school — charged only on a new admission, never on
 *  internal promotion. Forms 1–5 = P10,000; A-Level (AS & A2) = P5,000. */
export const DEV_FEE_SCHOOL = 10000;
export const DEV_FEE_ALEVEL = 5000;
export function developmentFeeFor(bandKey: string): number {
  return bandKey === 'AL' ? DEV_FEE_ALEVEL : DEV_FEE_SCHOOL;
}
export const DEVELOPMENT_FEE_LABEL = 'Development Fee';

export function structureMandatoryTotal(s: FeeStructure): number {
  return round2(s.lines.filter((l) => l.mandatory).reduce((a, l) => a + (l.amount || 0), 0));
}

/** Lines a student is charged: every mandatory line + any opted-in optional ones. */
export function resolveStudentCharges(structure: FeeStructure, optedInItemIds: string[] = []): InvoiceLine[] {
  const opt = new Set(optedInItemIds);
  return structure.lines
    .filter((l) => l.mandatory || (l.feeItemId != null && opt.has(l.feeItemId)))
    .map((l) => ({ feeItemId: l.feeItemId, label: l.label, category: l.category, amount: round2(l.amount) }));
}

export function newStructureId(year: string, bandKey: string): string {
  return `${year}_${bandKey}`;
}

/* ===================== billing run (P2) ============================ */
/**
 * The discount rules to apply for a run: the active configured rules,
 * or — if none are configured yet — defaults derived from settings, so
 * the sibling/prepayment policy works before the discount-config phase
 * (and before seeding).
 */
export function effectiveDiscountRules(rules: DiscountRule[], settings: FinanceSettings): DiscountRule[] {
  const active = rules.filter((r) => r.active);
  if (active.length > 0) return active;
  const ts = new Date(0).toISOString();
  const out: DiscountRule[] = [];
  if (settings.siblingDiscountPercent > 0) {
    out.push({ id: 'settings-sibling', kind: 'sibling', label: `Sibling discount (${settings.siblingDiscountPercent}% of tuition)`, active: true, percent: settings.siblingDiscountPercent, siblingOrderFrom: settings.siblingOrderFrom, appliesToCategory: 'tuition', createdAt: ts, updatedAt: ts });
  }
  if (settings.prepaymentDiscountPercent > 0) {
    out.push({ id: 'settings-prepay', kind: 'prepayment', label: `Prepayment discount (${settings.prepaymentDiscountPercent}%)`, active: true, percent: settings.prepaymentDiscountPercent, appliesToCategory: 'all', createdAt: ts, updatedAt: ts });
  }
  return out;
}

export interface BillingStudent { id: string; name: string; familyId?: string; bandKey: string; pendingDevFee?: number; status?: 'active' | 'left' | 'pending'; }

export interface BillingPlanRow {
  studentId: string;
  studentName: string;
  familyId?: string;
  bandKey: string;
  invoiceId: string;
  hasStructure: boolean;
  alreadyInvoiced: boolean;
  /** True when the band has a structure but isn't billed in this term. */
  notBilledThisTerm?: boolean;
  /** Family pays the year in advance (single up-front instalment). */
  prepay?: boolean;
  siblingRank?: number;
  lines: InvoiceLine[];
  discounts: InvoiceDiscount[];
  subtotal: number;
  discountTotal: number;
  total: number;
  /** One-time development fee added to this invoice (new admissions only). */
  devFee?: number;
}

export interface BillingBandSummary { bandKey: string; label: string; count: number; gross: number; discount: number; net: number; }

export interface BillingPlan {
  rows: BillingPlanRow[];
  byBand: BillingBandSummary[];
  counts: { total: number; toIssue: number; alreadyInvoiced: number; noStructure: number; notBilled: number; withSibling: number };
  totals: { gross: number; discount: number; net: number };
}

export interface BillingPlanInput {
  students: BillingStudent[];
  structures: FeeStructure[];
  rules: DiscountRule[];
  settings: FinanceSettings;
  /** Ids of the term's LIVE invoices — a pupil whose run invoice is here
   *  is already invoiced. Leave voided ones out: they go in the next set. */
  existingInvoiceIds: Set<string>;
  /** Ids of the term's VOIDED invoices. A voided run invoice no longer bills
   *  anyone, so the pupil is billable again — but the voided record keeps
   *  its id and its history, and the new invoice is written beside it as
   *  `${year}_${term}_${studentId}_r2` (then _r3…). Before 9 Oct 2026 a
   *  voided run invoice counted as "already invoiced", so the 20 pre-school
   *  children whose P22,000 invoices were voided could not be billed again. */
  voidedInvoiceIds?: Set<string>;
  year: string;
  term: TermKey;
  optedInByStudent?: Record<string, string[]>;
  /** Family ids that pay the full year in advance: prepay discount applies
   *  and the invoice is scheduled as a single up-front instalment. */
  prepayFamilyIds?: Set<string>;
  /** Families with an approved (active) sibling discount. Only these get
   *  the sibling reduction applied at billing. */
  approvedSiblingFamilyIds?: Set<string>;
  /**
   * Per-pupil concessions, if a caller ever wants billing to apply them.
   *
   * LKC DOES NOT PASS THIS, on purpose. The school bills at the full fee and
   * discounts afterwards from the Discounts screen, so applying concessions
   * here as well would grant every one of them twice. The parameter stays
   * because the arithmetic it drives (applyManualDiscounts) is the same
   * arithmetic that sizes the credit note, and a second school on this code
   * may well want the other order.
   */
  manualDiscountsByStudent?: Record<string, ManualDiscount[]>;
  /** Pupils covered by the staff-child benefit, from APPROVED staff-children
   *  links. They are not billed at all. The caller filters them out of
   *  `students`; this set is the belt behind that braces, in the same spirit
   *  as the 'pending' short-circuit below. */
  staffChildIds?: Set<string>;
  /** Per-pupil bursary %, from APPROVED awards only. A reduction of TUITION
   *  taken after the sibling discount, applied termly. */
  bursaryPctByStudent?: Record<string, number>;
}

/**
 * The id a billing run gives a pupil's term invoice: `${year}_${term}_${id}`,
 * or — when that one (and any re-issue after it) was voided — the next
 * `_r2`, `_r3`… so a re-issue never writes over a voided record.
 */
export function runInvoiceId(year: string, term: string, studentId: string, voided: Set<string>): string {
  const base = `${year}_${term}_${studentId}`;
  if (!voided.has(base)) return base;
  let n = 2;
  while (voided.has(`${base}_r${n}`)) n += 1;
  return `${base}_r${n}`;
}

/** Is this the billing run's own invoice for the pupil (first issue or a re-issue)? */
export function isRunInvoiceId(id: string, year: string, term: string, studentId: string): boolean {
  const base = `${year}_${term}_${studentId}`;
  if (id === base) return true;
  const rest = id.startsWith(`${base}_r`) ? id.slice(base.length + 2) : '';
  return /^\d+$/.test(rest);
}

/**
 * Compute, without writing anything, what a billing run would produce:
 * per-student invoice previews (tuition by band, sibling discounts
 * applied across families), a per-band summary, and counts of what is
 * issuable vs already invoiced vs blocked (no structure). Pure.
 */
export function planBillingRun(input: BillingPlanInput): BillingPlan {
  const { students, structures, rules, settings, existingInvoiceIds, year, term } = input;
  const voided = input.voidedInvoiceIds ?? new Set<string>();
  const eff = effectiveDiscountRules(rules, settings);

  // Sibling ranks within each family (stable by name).
  const byFamily: Record<string, BillingStudent[]> = {};
  for (const s of students) if (s.familyId) (byFamily[s.familyId] ||= []).push(s);
  const ranks: Record<string, number> = {};
  for (const fam of Object.values(byFamily)) {
    Object.assign(ranks, familySiblingRanks(fam.map((s) => ({ id: s.id, sortKey: s.name }))));
  }

  const structByBand: Record<string, FeeStructure> = {};
  for (const st of structures) if (st.year === year && st.active) structByBand[st.bandKey] = st;

  const rows: BillingPlanRow[] = students.map((s) => {
    const invoiceId = runInvoiceId(year, term, s.id, voided);
    const alreadyInvoiced = existingInvoiceIds.has(invoiceId);
    // BELT: a 'pending' student (Pastel-only, not yet enrolled) is never
    // billable — short-circuit to a non-billable row (same shape as the
    // no-structure branch) even if a caller forgets the query-level filter.
    // Nor is one who has LEFT: a pupil marked left keeps their last class,
    // and on 8 Oct 2026 the June billing run offered AS JUNE's 12 leavers
    // alongside its 17 pupils — 29 to invoice where the register has 17.
    if (s.status === 'pending' || s.status === 'left') {
      return { studentId: s.id, studentName: s.name, familyId: s.familyId, bandKey: s.bandKey, invoiceId, hasStructure: false, alreadyInvoiced, lines: [], discounts: [], subtotal: 0, discountTotal: 0, total: 0 };
    }
    // BELT: a staff member's child on an APPROVED link is not billed here at
    // all — the benefit is settled through payroll, not through an invoice
    // that is then discounted to nothing. The caller already filters these
    // out; this catches the case where it forgets.
    if (input.staffChildIds?.has(s.id)) {
      return { studentId: s.id, studentName: s.name, familyId: s.familyId, bandKey: s.bandKey, invoiceId, hasStructure: false, alreadyInvoiced, lines: [], discounts: [], subtotal: 0, discountTotal: 0, total: 0 };
    }
    // Most specific fee first, then whatever the bursar actually set up.
    const structure = bandChain(s.bandKey).map((k) => structByBand[k]).find(Boolean);
    if (!structure) {
      return { studentId: s.id, studentName: s.name, familyId: s.familyId, bandKey: s.bandKey, invoiceId, hasStructure: false, alreadyInvoiced, lines: [], discounts: [], subtotal: 0, discountTotal: 0, total: 0 };
    }
    if (structure.terms && structure.terms.length > 0 && !structure.terms.includes(term)) {
      return { studentId: s.id, studentName: s.name, familyId: s.familyId, bandKey: s.bandKey, invoiceId, hasStructure: true, alreadyInvoiced, notBilledThisTerm: true, lines: [], discounts: [], subtotal: 0, discountTotal: 0, total: 0 };
    }
    const lines = resolveStudentCharges(structure, input.optedInByStudent?.[s.id] ?? []);
    const subtotalBase = round2(lines.reduce((a, l) => a + l.amount, 0));
    const siblingRank = ranks[s.id];
    const prepay = !!(s.familyId && input.prepayFamilyIds?.has(s.familyId));
    const siblingApproved = !!(s.familyId && input.approvedSiblingFamilyIds?.has(s.familyId));
    const discounts = computeDiscounts(lines, { siblingRank, prepayFullYear: prepay, siblingApproved }, eff);
    /* The bursar's concessions, applied where the automatic sibling discount
       used to land — BEFORE the bursary, so the "bursary measured after the
       sibling discount" arithmetic below is unchanged by the switch. */
    const manual = input.manualDiscountsByStudent?.[s.id];
    if (manual && manual.length) discounts.push(...applyManualDiscounts(manual, lines));
    /* Bursary: a % of TUITION, taken AFTER the sibling discount.
       Order matters and is not arbitrary. Applying it to the gross tuition
       would hand a family with two siblings a larger bursary than the same
       award gives a family with one, because the two reductions would each
       be measured against the full figure and could together exceed it. */
    const bursaryPct = input.bursaryPctByStudent?.[s.id] ?? 0;
    if (bursaryPct > 0) {
      const tuitionBase = lineCategoryTotal(lines, 'tuition');
      // Counts the manual sibling concession as well as anything a rule
      // produced — they land in the same array and mean the same thing.
      const siblingOnTuition = round2(discounts.filter((d) => d.kind === 'sibling').reduce((a, d) => a + d.amount, 0));
      const afterSibling = Math.max(0, round2(tuitionBase - siblingOnTuition));
      const bursaryAmt = round2((afterSibling * Math.min(100, bursaryPct)) / 100);
      if (bursaryAmt > 0) discounts.push({ kind: 'bursary', label: `Bursary (${bursaryPct}%)`, amount: bursaryAmt, appliesToCategory: 'tuition' });
    }
    const discountTotal = round2(Math.min(subtotalBase, discounts.reduce((a, d) => a + d.amount, 0)));
    // One-time development fee (new admissions only) — added after discounts so
    // it's never reduced by the sibling/prepay discount.
    const devFee = s.pendingDevFee && s.pendingDevFee > 0 ? round2(s.pendingDevFee) : 0;
    const allLines = devFee > 0
      ? [...lines, { label: DEVELOPMENT_FEE_LABEL, category: 'other' as FeeCategory, amount: devFee }]
      : lines;
    const subtotal = round2(subtotalBase + devFee);
    const total = round2(subtotalBase - discountTotal + devFee);
    return { studentId: s.id, studentName: s.name, familyId: s.familyId, bandKey: s.bandKey, invoiceId, hasStructure: true, alreadyInvoiced, prepay, siblingRank, lines: allLines, discounts, subtotal, discountTotal, total, devFee };
  });

  const bandMap: Record<string, BillingBandSummary> = {};
  let toIssue = 0, alreadyInvoiced = 0, noStructure = 0, notBilled = 0, withSibling = 0, gross = 0, discount = 0, net = 0;
  for (const r of rows) {
    if (!r.hasStructure) { noStructure += 1; continue; }
    if (r.notBilledThisTerm) { notBilled += 1; continue; }
    if (r.alreadyInvoiced) { alreadyInvoiced += 1; continue; }
    toIssue += 1;
    if (r.discounts.some((d) => d.kind === 'sibling')) withSibling += 1;
    const bm = bandMap[r.bandKey] ?? (bandMap[r.bandKey] = { bandKey: r.bandKey, label: formBandLabel(r.bandKey), count: 0, gross: 0, discount: 0, net: 0 });
    bm.count += 1; bm.gross = round2(bm.gross + r.subtotal); bm.discount = round2(bm.discount + r.discountTotal); bm.net = round2(bm.net + r.total);
    gross = round2(gross + r.subtotal); discount = round2(discount + r.discountTotal); net = round2(net + r.total);
  }
  return {
    rows,
    byBand: Object.values(bandMap),
    counts: { total: students.length, toIssue, alreadyInvoiced, noStructure, notBilled, withSibling },
    totals: { gross, discount, net },
  };
}

/* ===================== payments (P3) =============================== */
/**
 * Apply a single payment to an invoice, incrementally. The invoice's
 * `paid` is the denormalised source of truth (kept current on every
 * capture), so we increment it rather than re-summing — and re-derive
 * balance + status. Pure; the UI persists the returned fields.
 */
export function applyPayment(invoice: Invoice, amount: number): { paid: number; balance: number; status: InvoiceStatus } {
  const paid = round2(invoice.paid + amount);
  /* AN ADJUSTMENT MUST SURVIVE THE NEXT PAYMENT. Rebuilding the balance
     from total and paid alone silently un-credits the invoice: a family
     given a P1,000 credit note who then pays P500 would find the balance
     back up by the P1,000 the school had already written off. The credit
     is on the invoice, so it has to be in the arithmetic that replaces
     the invoice's balance. */
  const adjusted = round2(Number(invoice.adjusted) || 0);
  const balance = round2(invoice.total - paid - adjusted);
  return {
    paid, balance,
    status: invoiceStatus(invoice.total, paid, invoice.status === 'void', adjusted),
  };
}

/** Receipt number, e.g. RCP/2026/00042. */
export function formatReceiptNo(year: string, seq: number): string {
  return `RCP/${year}/${String(seq).padStart(5, '0')}`;
}

/* ===================== account standing ===========================
 * ONE FIGURE PER PUPIL.
 *
 * A parent holding a credit from Term 2 and an invoice for Term 3 was being
 * shown two unrelated documents and left to do the subtraction. 259 pupils
 * hold a credit and a charge at the same time; 75 of them are net in credit
 * and were still sent a Term 3 invoice that reads as a debt.
 *
 * The reason there was no single figure is that a CREDIT AT THIS SCHOOL IS
 * CARRIED AS A NEGATIVE INVOICE. 803 of the 1,359 invoice documents are the
 * 11 Aug 2026 rebaseline of the Pastel age analysis (`OPEN/2026/...`, all
 * stamped Term2 2026), and 182 of those have a negative total: money the
 * school HOLDS, in a document shaped like money the school is OWED.
 *
 * So this function does not invent a new number. `balance` here is
 * algebraically the same total - paid - adjusted that buildFamilyStatement
 * already computed, and a test pins that identity. What it adds is NAMES for
 * the two halves that were previously summed together and lost:
 *
 *     charged        what was billed          (documents with a positive total)
 *     heldOnAccount  what the school holds    (documents with a negative total)
 *
 * Once those are separate, a report can say what a term actually billed
 * without P3.08m of brought-forward Pastel credits netted into it, and a
 * parent can be shown one honest line instead of two halves of a sum.
 *
 * THE PUPIL IS THE UNIT, NOT THE FAMILY. 694 of 808 live invoices carry no
 * familyId - the Pastel load brought a studentId and nothing else - so a
 * family-keyed figure is blind to most of the money. Families are a rollup
 * of pupils (combineStandings), never the source.
 *
 * Read from invoice.paid / invoice.adjusted rather than from the payment and
 * adjustment collections, deliberately: those denormalised fields are what
 * the invoice list, the aging and the reminders all trust, so a standing
 * derived from anything else would disagree with every screen beside it.
 * ================================================================== */

/**
 * The day the school's own books start.
 *
 * On 11 Aug 2026 the Pastel age analysis was rebaselined into this system as
 * 803 `OPEN/2026/...` documents. Everything before that date is history the
 * school inherited; everything after it, the school billed. The date belongs
 * in one place because it appears on parent-facing screens, and a date that
 * is typed out on four screens is a date that will read differently on one
 * of them.
 */
export const OPENING_BALANCE_DATE = '2026-08-11';
/** How that date is said to a parent. */
export const OPENING_BALANCE_LABEL = '11 August 2026';

/**
 * Is this a position brought forward rather than something the school billed?
 *
 * Reads the tag when it is there and the reference when it is not, so the
 * answer is the same before and after the backfill and no reader has to know
 * which. `OPEN/` is how every one of the 803 Pastel documents is numbered,
 * and nothing else in the collection uses that prefix.
 */
export function isOpeningBalance(inv: Pick<Invoice, 'kind' | 'invoiceNo'>): boolean {
  if (inv.kind) return inv.kind === 'opening';
  return String(inv.invoiceNo ?? '').startsWith('OPEN/');
}

/** A position, with the halves that make it up named. */
export interface Standing {
  /** Billed: the total of every document that charges something. */
  charged: number;
  /** Held: the total of every document that is a credit, as a POSITIVE. */
  heldOnAccount: number;
  /** Money received. */
  paid: number;
  /** Settled without money arriving - credit notes and write-offs, less
   *  refunds, which hand the credit back as cash and so put the debt up. */
  adjusted: number;
  /** charged - heldOnAccount - paid - adjusted. Positive = owes the school. */
  balance: number;
  /** The balance as a debt, or 0. For "you owe" on a screen. */
  owing: number;
  /** The balance as a credit, or 0. For "in credit" on a screen. */
  inCredit: number;
  /**
   * How much of the balance came from before the school kept its own books
   * — the net of the brought-forward documents, positive for a debt carried
   * in and negative for a credit carried in.
   *
   * Worth its own field because it is the sentence the office keeps having to
   * say on the telephone: of your P30,000, P8,000 is from before August. It
   * is a part of `balance`, not an addition to it.
   */
  broughtForward: number;
  /** When this was computed. A standing is a position at a moment. */
  asAt: string;
}

export interface AccountStanding extends Standing {
  studentId: string;
  /** How many live documents it was drawn from - 0 means no account yet,
   *  which is not the same as a settled one. */
  documentCount: number;
}

interface StandingAcc {
  charged: number; heldOnAccount: number; paid: number; adjusted: number; broughtForward: number;
}
const ZERO_STANDING: StandingAcc = {
  charged: 0, heldOnAccount: 0, paid: 0, adjusted: 0, broughtForward: 0,
};

/** Fold one document into a running position. */
function addDocument(acc: StandingAcc, i: Invoice): void {
  const total = Number(i.total) || 0;
  // The sign of the document decides which half it belongs to. A negative
  // total is not a charge of less than nothing; it is a credit.
  if (total < 0) acc.heldOnAccount -= total; else acc.charged += total;
  const paid = Number(i.paid) || 0;
  const adjusted = Number(i.adjusted) || 0;
  acc.paid += paid;
  acc.adjusted += adjusted;
  // Its contribution to the balance, not its face value: an opening debt
  // that has since been paid is no longer part of what is carried in.
  if (isOpeningBalance(i)) acc.broughtForward += total - paid - adjusted;
}

/** Positive = owes, negative = in credit, and the two readable forms of it. */
function closeStanding(acc: StandingAcc, asAt: string): Standing {
  const charged = round2(acc.charged);
  const heldOnAccount = round2(acc.heldOnAccount);
  const paid = round2(acc.paid);
  const adjusted = round2(acc.adjusted);
  const balance = round2(charged - heldOnAccount - paid - adjusted);
  return {
    charged,
    heldOnAccount,
    paid,
    adjusted,
    balance,
    owing: balance > 0 ? balance : 0,
    inCredit: balance < 0 ? round2(-balance) : 0,
    broughtForward: round2(acc.broughtForward),
    asAt,
  };
}

/**
 * What one pupil's account actually stands at, across every document on it.
 *
 * Pass the pupil's invoices, or all of them - it filters. Void documents are
 * ignored: a voided invoice was never a charge.
 */
export function accountStanding(
  studentId: string,
  invoices: Invoice[],
  asAt: Date = new Date(),
): AccountStanding {
  const mine = invoices.filter((i) => i.studentId === studentId && i.status !== 'void');
  const acc = { ...ZERO_STANDING };
  for (const i of mine) addDocument(acc, i);
  return {
    studentId,
    documentCount: mine.length,
    ...closeStanding(acc, asAt.toISOString()),
  };
}

/**
 * Every pupil's standing in one pass, keyed by studentId. For a screen that
 * needs the whole school - arrears, the fees list - rather than 826 filters.
 */
export function accountStandings(
  invoices: Invoice[],
  asAt: Date = new Date(),
): Map<string, AccountStanding> {
  const stamp = asAt.toISOString();
  const acc = new Map<string, StandingAcc & { n: number }>();
  for (const i of invoices) {
    if (i.status === 'void') continue;
    let a = acc.get(i.studentId);
    if (!a) { a = { ...ZERO_STANDING, n: 0 }; acc.set(i.studentId, a); }
    addDocument(a, i);
    a.n += 1;
  }
  const out = new Map<string, AccountStanding>();
  acc.forEach((a, studentId) => {
    out.set(studentId, { studentId, documentCount: a.n, ...closeStanding(a, stamp) });
  });
  return out;
}

/**
 * Add standings together - the siblings of a household, or a whole year group.
 *
 * Summing the PARTS and re-deriving the balance, rather than summing the
 * balances, so a credit on one child and a debt on another still leave both
 * halves visible in the result. A household that has paid P30,000 and been
 * billed P30,000 is square, but "square on P30,000" and "square on nothing"
 * are not the same fact and the school needs to tell them apart.
 */
export function combineStandings(rows: Standing[], asAt: Date = new Date()): Standing {
  const acc = { ...ZERO_STANDING };
  for (const r of rows) {
    acc.charged += r.charged;
    acc.heldOnAccount += r.heldOnAccount;
    acc.paid += r.paid;
    acc.adjusted += r.adjusted;
    acc.broughtForward += r.broughtForward;
  }
  return closeStanding(acc, asAt.toISOString());
}

/* ===================== family statements (P4) ===================== */
export interface StatementMember {
  studentId: string;
  name: string;
  invoices: Invoice[];
  billed: number;
  paid: number;
  /** Settled without money arriving — credit notes and write-offs. */
  adjusted: number;
  balance: number;
}
export interface StatementTxn {
  date: string;
  kind: 'invoice' | 'payment' | 'adjustment';
  ref: string;
  description: string;
  charge: number;
  credit: number;
  balance: number; // running
}
export interface StatementScheduleRow {
  studentName: string;
  label: string;
  dueDate: string;
  amount: number;
  balance: number;
  status: InstalmentState['status'];
}
export interface FamilyStatement {
  familyId: string;
  familyName: string;
  contactName?: string;
  contactEmail?: string;
  members: StatementMember[];
  txns: StatementTxn[];
  billed: number;
  paid: number;
  /** Settled without money arriving — credit notes and write-offs, less
   *  any refund that handed a credit back as cash. */
  adjusted: number;
  balance: number;
  aging: Aging;
  /** Forward-looking unpaid instalments across the family's open invoices,
   *  soonest first — so the statement shows what's due when, and what's overdue. */
  schedule: StatementScheduleRow[];
}
/** Just the fields a statement needs, so financeCore does not depend on
 *  lib/adjustments. `kind` decides direction — never the sign. */
export interface StatementAdjustment {
  id: string;
  adjustmentNo?: string;
  kind: 'credit_note' | 'write_off' | 'refund';
  studentId?: string;
  invoiceId?: string;
  amount: number;
  reason?: string;
  date?: string;
}

/**
 * A REFUND IS NOT A CREDIT.
 *
 * All three reduce something, which is why they share a collection, but
 * only two of them reduce what the FAMILY OWES. A refund hands money back
 * that the family had already overpaid: the cash leaves, and the credit
 * that money represented leaves with it, so the debtor balance goes UP.
 *
 * Treating all three as credits reads a refund as a second discount and
 * understates the account by twice the refund.
 */
export function adjustmentReducesBalance(kind: StatementAdjustment['kind']): boolean {
  return kind !== 'refund';
}

export interface FamilyStatementInput {
  familyId: string;
  familyName: string;
  contactName?: string;
  contactEmail?: string;
  members: { studentId: string; name: string }[];
  invoices: Invoice[];
  payments: Payment[];
  /** Credit notes, write-offs and refunds. Optional so a caller that has
   *  not loaded them still gets correct TOTALS — those come from each
   *  invoice's own `adjusted` field — but the ledger cannot then name
   *  what reduced the account. */
  adjustments?: StatementAdjustment[];
  /**
   * WHO IS GOING TO READ THIS.
   *
   * An adjustment's `reason` is written by staff for staff. On a credit
   * note it is the concession itself — "Sibling discount — siblings" — and
   * the family should see it. On a WRITE-OFF it is the school's own note
   * about a debt it has decided not to pursue, and putting that in front
   * of the family is neither kind nor wise.
   *
   * Defaults to 'staff', so every existing caller is unchanged.
   */
  audience?: 'staff' | 'parent';
  settings: FinanceSettings;
  asOf?: Date;
}

/**
 * Aggregate one household's account: every child's invoices and the
 * payments against them, a single combined balance, a chronological
 * ledger (charges + credits with a running balance), and arrears aging.
 * Pure — the UI resolves member names and the family/contact identity.
 */
export function buildFamilyStatement(input: FamilyStatementInput): FamilyStatement {
  const memberIds = new Set(input.members.map((m) => m.studentId));
  const famInvoices = input.invoices.filter(
    (i) => i.status !== 'void' && (i.familyId === input.familyId || memberIds.has(i.studentId)),
  );
  const invIds = new Set(famInvoices.map((i) => i.id));
  const famPayments = input.payments.filter((p) => invIds.has(p.invoiceId));
  const famAdjustments = (input.adjustments ?? []).filter(
    (a) => (a.invoiceId && invIds.has(a.invoiceId)) || (a.studentId && memberIds.has(a.studentId)),
  );

  /* Taken from the INVOICES, not from the adjustment documents, so the
     total always agrees with the balance the rest of the system reads —
     arrears, aging, reminders. */
  const adjusted = round2(famInvoices.reduce((a, i) => a + (Number(i.adjusted) || 0), 0));

  const nameByStudent: Record<string, string> = {};
  input.members.forEach((m) => { nameByStudent[m.studentId] = m.name; });

  /* ONE DEFINITION OF WHAT A PUPIL OWES, not a second copy of it.
     accountStanding does the arithmetic this block used to do inline, and
     returns the same figures - `billed` is charged less what is held, which
     is the sum of the totals either way. What it buys is that the statement,
     the portal and the arrears run can no longer drift apart, because there
     is only one place left to change.

     The subtraction of `adjusted` is the part that was once missing here: a
     credit note settles an invoice without a payment, and recomputing
     `billed - paid` threw that away and re-billed a family for a discount
     the bursar had granted. It now lives inside accountStanding. */
  const members: StatementMember[] = input.members
    .map((m) => {
      const mine = famInvoices.filter((i) => i.studentId === m.studentId);
      const st = accountStanding(m.studentId, mine);
      return {
        studentId: m.studentId, name: m.name, invoices: mine,
        billed: round2(st.charged - st.heldOnAccount),
        paid: st.paid, adjusted: st.adjusted, balance: st.balance,
      };
    })
    .filter((m) => m.invoices.length > 0);

  const raw: Omit<StatementTxn, 'balance'>[] = [
    ...famInvoices.map((i) => ({
      date: i.issuedAt, kind: 'invoice' as const, ref: i.invoiceNo,
      description: `Invoice — ${nameByStudent[i.studentId] ?? i.studentName ?? ''} (${termLabelForCohort(String(i.term), i.cohort)} ${i.year})`,
      charge: i.total, credit: 0,
    })),
    ...famPayments.map((p) => ({
      date: p.receivedAt, kind: 'payment' as const, ref: p.receiptNo ?? '',
      description: `${p.isReversal || p.amount < 0 ? 'Payment reversal' : 'Payment'} — ${nameByStudent[p.studentId] ?? p.studentName ?? ''}${p.method ? ` (${p.method})` : ''}`,
      charge: 0, credit: p.amount,
    })),
    /* A reduction the family cannot see is a reduction they will query.
       Billed 1,000 / paid 0 / balance 0 reads as a mistake unless the
       statement says what happened in between. */
    ...famAdjustments.map((a) => {
      const reduces = adjustmentReducesBalance(a.kind);
      const toParent = input.audience === 'parent';
      /* A family is told a credit was applied, not that the school has
         written their debt off. Same money, same balance; the second is
         an internal decision and saying it out loud invites a conversation
         nobody at the school chose to start. */
      const label = a.kind === 'credit_note' ? 'Credit note'
        : a.kind === 'write_off' ? (toParent ? 'Credit to your account' : 'Write-off')
          : 'Refund';
      /* Only a credit note's reason is the concession itself and safe to
         show. Everything else is a staff note. */
      const showReason = a.reason && (!toParent || a.kind === 'credit_note');
      return {
        date: a.date ?? '', kind: 'adjustment' as const, ref: a.adjustmentNo ?? '',
        description: `${label} — ${nameByStudent[a.studentId ?? ''] ?? ''}${showReason ? ` (${a.reason})` : ''}`,
        charge: reduces ? 0 : round2(a.amount),
        credit: reduces ? round2(a.amount) : 0,
      };
    }),
  ].sort((a, b) => (a.date || '').localeCompare(b.date || ''));

  /* A LEDGER THAT DOES NOT REACH THE STATED BALANCE IS WORSE THAN NO
     LEDGER, because somebody adds it up and finds the school's own
     document disagreeing with itself. The totals come from the invoices;
     these lines come from the adjustment documents. The gap between them
     is shown as one honest line rather than left for a parent to find.

     It is deliberately worded to claim nothing about WHY it is not
     itemised, because there are two reasons and the statement cannot tell
     them apart: an imported correction that predates credit notes, or a
     real credit note the reader is not allowed to see — the parent portal
     reads invoices and payments but not the adjustments collection. */
  const itemised = round2(famAdjustments.reduce(
    (a, x) => a + (adjustmentReducesBalance(x.kind) ? x.amount : -x.amount), 0,
  ));
  const unitemised = round2(adjusted - itemised);
  if (Math.abs(unitemised) > 0.005) {
    raw.push({
      date: raw.length ? raw[raw.length - 1].date : '',
      kind: 'adjustment' as const, ref: '',
      description: 'Adjustment applied to the account',
      charge: unitemised < 0 ? round2(-unitemised) : 0,
      credit: unitemised > 0 ? unitemised : 0,
    });
  }

  let running = 0;
  const txns: StatementTxn[] = raw.map((t) => { running = round2(running + t.charge - t.credit); return { ...t, balance: running }; });

  const billed = round2(famInvoices.reduce((a, i) => a + i.total, 0));
  const paid = round2(famInvoices.reduce((a, i) => a + i.paid, 0));
  const asOf = input.asOf ?? new Date();
  const schedule: StatementScheduleRow[] = famInvoices
    .flatMap((i) =>
      instalmentStates(i, asOf)
        .filter((st) => st.balance > 0)
        .map((st) => ({
          studentName: nameByStudent[i.studentId] ?? i.studentName ?? '',
          label: st.label,
          dueDate: st.dueDate,
          amount: st.amount,
          balance: st.balance,
          status: st.status,
        })),
    )
    .sort((a, b) => (a.dueDate || '').localeCompare(b.dueDate || ''));
  return {
    familyId: input.familyId, familyName: input.familyName,
    contactName: input.contactName, contactEmail: input.contactEmail,
    members, txns, billed, paid, adjusted, balance: round2(billed - paid - adjusted),
    aging: ageInvoices(famInvoices, input.settings.agingThresholds, input.asOf),
    schedule,
  };
}

/* ===================== arrears + reminders (P7) ==================== */
/**
 * Simple late interest on one invoice: monthlyRate% × outstanding
 * balance × completed months overdue. A part-month doesn't accrue until
 * it completes (so a freshly-overdue invoice carries none yet).
 */
export function lateInterestForInvoice(inv: Invoice, monthlyRatePct: number, asOf: Date = new Date()): number {
  if (inv.status === 'void' || inv.balance <= 0 || monthlyRatePct <= 0) return 0;
  const months = Math.floor(daysPastDue(inv.dueDate, asOf) / 30);
  if (months <= 0) return 0;
  return round2(inv.balance * (monthlyRatePct / 100) * months);
}

/**
 * WHAT A PUPIL IS ACTUALLY OVERDUE FOR, once their own credits are applied.
 *
 * A credit lives on its own invoice document — an opening balance brought
 * over as a negative, a credit note, an overpayment carried forward. Read
 * one invoice at a time that credit is invisible: the pupil's other invoice
 * is past due for its full amount and the arrears list chases it. Two
 * pupils were being chased for P36,500 while holding credits of P58,000
 * and P26,500 — the office knew they owed nothing and the screen insisted
 * otherwise.
 *
 * `familyHasArrears` already carries this guard, which is why the parent
 * portal never wrongly locked either of them out of academics. The listing
 * simply never had it.
 *
 * CREDITS GO AGAINST THE OLDEST ITEM FIRST, which is how a ledger allocates
 * and how the office would do it by hand. A credit smaller than the debt
 * leaves the remainder overdue at its original age — it does not reset the
 * clock, because that money is still late.
 */
export interface OverdueRow {
  invoice: Invoice;
  /** Past-due balance after this pupil's credits have been applied. */
  overdue: number;
  /** Days past due of the oldest overdue instalment on this invoice. */
  oldest: number;
}

export function overdueRows(invoices: Invoice[], asOf: Date = new Date()): OverdueRow[] {
  const live = invoices.filter((i) => i.status !== 'void');

  /* What each pupil holds on account, wherever it happens to sit.
     NOT accountStanding's heldOnAccount, and the difference matters. That one
     is the pupil's whole credit position, for telling them where they stand.
     This one is drawn from unsettled BALANCES, because the question here is
     narrower: which specific overdue debts can be extinguished right now. A
     credit already consumed by a charge cannot be spent twice on another. */
  const credit = new Map<string, number>();
  for (const i of live) {
    const bal = Number(i.balance) || 0;
    if (bal < 0) credit.set(i.studentId, round2((credit.get(i.studentId) ?? 0) - bal));
  }

  const rows: OverdueRow[] = [];
  for (const invoice of live) {
    const states = instalmentStates(invoice, asOf).filter((x) => x.status === 'overdue');
    const overdue = round2(states.reduce((a, x) => a + x.balance, 0));
    if (overdue <= 0) continue;
    rows.push({ invoice, overdue, oldest: states.reduce((m, x) => Math.max(m, x.daysPastDue), 0) });
  }

  if (credit.size === 0) return rows;

  // Oldest first, so a part-credit leaves the newest debt standing.
  const ordered = [...rows].sort((a, b) => b.oldest - a.oldest
    || a.invoice.dueDate.localeCompare(b.invoice.dueDate));

  for (const row of ordered) {
    const pot = credit.get(row.invoice.studentId) ?? 0;
    if (pot <= 0) continue;
    const used = Math.min(pot, row.overdue);
    row.overdue = round2(row.overdue - used);
    credit.set(row.invoice.studentId, round2(pot - used));
  }

  return rows.filter((r) => r.overdue > 0);
}

export interface ArrearsFamily {
  familyId: string;
  overdue: number;
  interest: number;
  invoiceCount: number;
  studentIds: string[];
  oldestDays: number;
  aging: Aging;
}
export interface ArrearsPlan {
  families: ArrearsFamily[];
  totalOverdue: number;
  totalInterest: number;
  familyCount: number;
}
export interface ArrearsInput {
  invoices: Invoice[];
  settings: FinanceSettings;
  monthlyRatePct?: number; // overrides settings
  asOf?: Date;
}

/**
 * Roll overdue invoices up per household: one arrears row per family
 * (combining every child's past-due invoices), with projected late
 * interest, aging, and the oldest days-past-due. Pure.
 */
export function planArrears(input: ArrearsInput): ArrearsPlan {
  const asOf = input.asOf ?? new Date();
  const rate = input.monthlyRatePct ?? input.settings.lateInterestMonthlyPct ?? 0;

  // Instalment-aware and credit-aware: a future part of a split invoice is
  // not overdue, and neither is a debt the pupil already holds the money for.
  const rows = overdueRows(input.invoices, asOf);

  const byFamily: Record<string, OverdueRow[]> = {};
  for (const r of rows) {
    const fid = r.invoice.familyId || `__${r.invoice.studentId}`;
    (byFamily[fid] ||= []).push(r);
  }

  const families: ArrearsFamily[] = Object.entries(byFamily).map(([familyId, rs]) => {
    const overdueAmt = round2(rs.reduce((a, x) => a + x.overdue, 0));
    const oldestDays = rs.reduce((m, x) => Math.max(m, x.oldest), 0);
    const interest = round2(rs.reduce((a, x) => a + (rate > 0 ? x.overdue * (rate / 100) * Math.floor(x.oldest / 30) : 0), 0));
    return {
      familyId,
      overdue: overdueAmt,
      interest,
      invoiceCount: rs.length,
      studentIds: [...new Set(rs.map((r) => r.invoice.studentId))],
      oldestDays,
      // Aged on the netted figure, so the buckets and the total agree with
      // what the family is being asked for.
      aging: ageInvoices(
        rs.map((r) => ({ ...r.invoice, balance: r.overdue })),
        input.settings.agingThresholds, asOf,
      ),
    };
  }).sort((a, b) => b.oldestDays - a.oldestDays || b.overdue - a.overdue);

  return {
    families,
    totalOverdue: round2(families.reduce((a, f) => a + f.overdue, 0)),
    totalInterest: round2(families.reduce((a, f) => a + f.interest, 0)),
    familyCount: families.length,
  };
}

/* ------------------------------------------------------------------
 * ACADEMIC HOLD (Phase 3)
 * ------------------------------------------------------------------
 * When a family is in arrears (an invoice is past due with a balance),
 * their parents' access to academic information is paused — the parent
 * portal shows only Fees until it's resolved. A finance admin can
 * override PER PUPIL: 'release' (never hold, e.g. bursary) or 'hold'
 * (force). Absence / 'auto' = automatic from arrears.
 *
 * PER PUPIL, NOT PER FAMILY. LKC does not use families. It has the
 * collection and most pupils have an id in it, but nothing is administered
 * at that level and 694 of 808 live invoices carry no familyId at all -
 * the opening balances brought over from Pastel were loaded with a
 * studentId and nothing else.
 *
 * That mattered more than it sounds. The hold used to read invoices
 * `where familyId == ...`, so an invoice with no familyId belonged to
 * nobody and was never seen: 227 invoices owing P5.58m resolved to TWO
 * families in arrears and exactly ONE pupil held, out of 826. The gate was
 * switched on, correctly written, and blind.
 *
 * Keying on the pupil fixes that without touching a single invoice, because
 * every invoice already carries a studentId. It is also what the school
 * actually means: one child's unpaid fees pause THAT child's reports, and a
 * sibling who is paid up is unaffected.
 * ---------------------------------------------------------------- */
export type AcademicHoldMode = 'auto' | 'release' | 'hold';

/** Grace period before arrears triggers an academic hold. 0 = the day
 *  after the due date. Bump this for a softer policy. */
export const ACADEMIC_HOLD_GRACE_DAYS = 0;

/**
 * True only when the family actually OWES money overall AND some of that is
 * past due. The net guard is essential: a family whose credit on one invoice
 * cancels a debt on another (or that has a zero balance, or no invoices at
 * all) is NEVER held — even if a single invoice looks individually overdue.
 * So only parents with a real, active outstanding balance are gated.
 */
export function familyHasArrears(
  invoices: Invoice[],
  asOf: Date = new Date(),
  graceDays: number = ACADEMIC_HOLD_GRACE_DAYS,
): boolean {
  const live = invoices.filter((i) => i.status !== 'void');
  const net = round2(live.reduce((s, i) => s + (i.balance || 0), 0));
  if (net <= 0) return false; // owes nothing overall → never held
  return live.some((i) => i.balance > 0 && daysPastDue(i.dueDate, asOf) > graceDays);
}

/**
 * True when THIS PUPIL owes money overall AND some of it is past due.
 *
 * The net guard is the same one familyHasArrears carries and matters just as
 * much here: a pupil whose credit on one invoice cancels a debt on another,
 * or who has a zero balance, or no invoices at all, is NEVER held - even if
 * a single invoice looks individually overdue. Only a real, active
 * outstanding balance gates anything.
 *
 * `invoices` may be the parent's whole scoped set across several children;
 * it is filtered to this pupil here so callers cannot forget to.
 */
export function studentHasArrears(
  invoices: Invoice[],
  studentId: string,
  asOf: Date = new Date(),
  graceDays: number = ACADEMIC_HOLD_GRACE_DAYS,
): boolean {
  if (!studentId) return false;
  return familyHasArrears(
    invoices.filter((i) => i.studentId === studentId),
    asOf,
    graceDays,
  );
}

/**
 * Doc id in `financeAcademicHolds` for a pupil's override.
 *
 * The pupil id IS the doc id. Trivial today, but named so that the one place
 * the key is decided is greppable - the collection previously held family
 * ids, and a stray familyId key would silently never be read again.
 */
export function holdDocIdForStudent(studentId: string): string {
  return studentId;
}

/** Resolve the effective hold from auto-arrears + a manual override. */
export function effectiveAcademicHold(
  hasArrears: boolean,
  mode: AcademicHoldMode = 'auto',
): boolean {
  if (mode === 'release') return false;
  if (mode === 'hold') return true;
  return hasArrears;
}

/** Sentinel doc id in financeAcademicHolds holding the SCHOOL-WIDE default
 *  hold mode. Real family ids are 20–21 char auto-ids, so this never
 *  collides. NB: must NOT match Firestore's reserved `__.*__` pattern. */
export const GLOBAL_HOLD_ID = 'globalHoldSetting';

/** Combine the school-wide default with a per-family override. A per-family
 *  setting (hold/release) ALWAYS wins over the global default, so admins can
 *  relax everyone yet still force-hold one family (or vice-versa). */
export function resolveHoldMode(
  perFamily: AcademicHoldMode = 'auto',
  globalMode: AcademicHoldMode = 'auto',
): AcademicHoldMode {
  return perFamily !== 'auto' ? perFamily : globalMode;
}

/* ------------------------------------------------------------------
 * JOINING INVOICE (admissions → finance)
 * ------------------------------------------------------------------
 * A mid-year (or any) new student's first invoice = the current term's
 * structure fees (full term by default; a factor < 1 pro-rates them)
 * PLUS the mandatory one-time/annual catalog fees (development, levies).
 * Application + registration fees are collected up front at approval and
 * are NOT included here. Mirrored in the admitApplication function.
 * ---------------------------------------------------------------- */
export interface OneTimeFee {
  feeItemId?: string;
  label: string;
  category: FeeCategory;
  amount: number;
}
export function joiningInvoiceLines(input: {
  structureLines: FeeStructureLine[];
  oneTimeFees: OneTimeFee[];
  termFeeFactor?: number;
}): InvoiceLine[] {
  const factor =
    input.termFeeFactor == null ? 1 : Math.max(0, Math.min(1, input.termFeeFactor));
  const termLines: InvoiceLine[] = input.structureLines
    .filter((l) => l.mandatory)
    .map((l) => ({
      feeItemId: l.feeItemId,
      label: factor < 1 ? `${l.label} (${Math.round(factor * 100)}%)` : l.label,
      category: l.category,
      amount: round2((l.amount || 0) * factor),
    }));
  const onceLines: InvoiceLine[] = input.oneTimeFees.map((f) => ({
    feeItemId: f.feeItemId,
    label: f.label,
    category: f.category,
    amount: round2(f.amount || 0),
  }));
  return [...termLines, ...onceLines];
}

/* ===================== sibling-discount approval ====================== *
 * The sibling discount is withheld until the family's ELDEST child has a
 * fully-settled account (whole-account balance <= 0), then surfaced for a
 * finance admin to approve. A weekly Cloud Function raises `pending`
 * records; approval applies the reduction and writes an `applied` record.
 * -------------------------------------------------------------------- */
export interface SiblingBeneficiary { studentId: string; studentName: string }

export interface PendingSiblingDiscount {
  id: string;
  familyId: string;
  eldestId: string;
  eldestName: string;
  eldestBalance: number;        // whole-account balance at detection (<= 0)
  beneficiaries: SiblingBeneficiary[];
  ruleId: string;               // the active sibling DiscountRule
  status: 'pending' | 'approved' | 'rejected';
  detectedAt: string;
  decidedAt?: string;
  decidedBy?: string;
}

export interface AppliedSiblingDiscount {
  id: string;                   // = familyId (one active discount per family)
  familyId: string;
  eldestId: string;
  ruleId: string;
  beneficiaries: SiblingBeneficiary[];
  appliedInvoiceIds: string[];  // invoices credited at approval (for reversal)
  status: 'active' | 'flagged' | 'removed';
  approvedAt: string;
  approvedBy: string;
  flaggedAt?: string;           // set by the weekly scan if eldest re-enters arrears
}

/**
 * Put a discount ON an invoice that has already been raised, and give back
 * the fields that changes.
 *
 * WHY THIS IS NOT AUTOMATICALLY A CREDIT NOTE. Reducing an issued invoice
 * is a credit note when the invoice has already POSTED: the journal exists,
 * it says the school billed the full amount, and you cannot un-say a
 * journal — you reverse it with another one. That reasoning is sound and it
 * is why `creditIssuedInvoice` exists.
 *
 * It just does not describe LKC yet. Nothing has posted: `financeGlJournals`
 * is empty and the cut-over date has not been named. There is no journal to
 * contradict, so a sibling discount recorded today can be what the school
 * actually means by it — a discount, on the bill, reducing what was charged
 * — instead of a reversal of a charge that was never posted.
 *
 * It also posts BETTER. When the cut-over comes, an invoice carrying a
 * discount line raises two journals: income gross, and the discount to 4900
 * Discounts & Bursaries Granted. That is the same account the credit note
 * routes to, reached without a second document, and the invoice's own total
 * then matches what the family was asked to pay.
 *
 * SUBTOTAL IS TAKEN FROM THE INVOICE, NOT RE-DERIVED FROM ITS LINES. 629 of
 * these are balances brought over from Pastel and some carry no breakdown at
 * all; re-summing an empty `lines` array would silently rewrite a P34,000
 * bill to nil. We are adding a reduction, not rebuilding the charge.
 */
export function addDiscountToInvoice(
  inv: Pick<Invoice, 'discounts' | 'lines' | 'subtotal' | 'total' | 'discountTotal' | 'paid' | 'adjusted' | 'status'>,
  entry: InvoiceDiscount,
): Pick<Invoice, 'discounts' | 'subtotal' | 'discountTotal' | 'total' | 'balance' | 'status'> {
  const discounts = [...(inv.discounts ?? []), entry];

  const fromLines = round2((inv.lines ?? []).reduce((a, l) => a + (Number(l.amount) || 0), 0));
  const subtotal = round2(Number(inv.subtotal) || fromLines
    || round2((Number(inv.total) || 0) + (Number(inv.discountTotal) || 0)));

  /* A discount can take a bill to nothing and never past it. An invoice
     pushed negative reads as the school owing a family money it never
     received. */
  const discountTotal = round2(Math.min(
    subtotal,
    discounts.reduce((a, d) => a + (Number(d.amount) || 0), 0),
  ));
  const total = round2(subtotal - discountTotal);
  const paid = round2(Number(inv.paid) || 0);
  const adjusted = round2(Number(inv.adjusted) || 0);

  return {
    discounts,
    subtotal,
    discountTotal,
    total,
    balance: round2(total - paid - adjusted),
    status: invoiceStatus(total, paid, inv.status === 'void', adjusted),
  };
}

/** Reduction (positive number) a sibling rule yields against a set of lines. */
export function siblingDiscountAmount(rule: DiscountRule, lines: InvoiceLine[]): number {
  const cat = rule.appliesToCategory ?? 'tuition';
  const base = lineCategoryTotal(lines, cat);
  if (base <= 0) return 0;
  const amt = rule.flat != null ? Math.min(rule.flat, base) : round2((base * (rule.percent ?? 0)) / 100);
  return amt > 0 ? round2(amt) : 0;
}

function recomputeTotals(inv: Invoice): Invoice {
  const subtotal = round2(inv.lines.reduce((a, l) => a + (l.amount || 0), 0));
  const discountTotal = round2(Math.min(subtotal, inv.discounts.reduce((a, d) => a + (d.amount || 0), 0)));
  const total = round2(subtotal - discountTotal);
  /* Same rule as applyPayment: adding a sibling discount to an invoice
     that already carries a credit note must not discard the credit. */
  const adjusted = round2(Number(inv.adjusted) || 0);
  const balance = round2(total - (inv.paid || 0) - adjusted);
  const status: InvoiceStatus = invoiceStatus(total, inv.paid || 0, inv.status === 'void', adjusted);
  return { ...inv, subtotal, discountTotal, total, balance, status };
}

/** Add (or refresh) the sibling discount on an invoice. Idempotent per ruleId. */
export function applySiblingDiscountToInvoice(inv: Invoice, rule: DiscountRule): Invoice {
  const amount = siblingDiscountAmount(rule, inv.lines);
  const discounts = inv.discounts.filter((d) => !(d.kind === 'sibling' && d.ruleId === rule.id));
  if (amount > 0) discounts.push({ ruleId: rule.id, kind: 'sibling', label: rule.label, amount });
  return recomputeTotals({ ...inv, discounts });
}

/** Remove the sibling discount (by rule) from an invoice and re-total. */
export function removeSiblingDiscountFromInvoice(inv: Invoice, ruleId: string): Invoice {
  const discounts = inv.discounts.filter((d) => !(d.kind === 'sibling' && d.ruleId === ruleId));
  return recomputeTotals({ ...inv, discounts });
}

/** Pick a student's most recent non-void invoice (latest year, then term). */
export function latestTermInvoice(invoices: Invoice[]): Invoice | undefined {
  const order: Record<string, number> = { Term1: 1, Term2: 2, Term3: 3 };
  return invoices
    .filter((i) => i.status !== 'void')
    .sort((a, b) => (b.year.localeCompare(a.year)) || ((order[b.term] ?? 0) - (order[a.term] ?? 0)))[0];
}
